import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../stores/RootStore';
// TODO: Import necessary icons

// --- Import Icons (Example using Heroicons - install @heroicons/react) ---
import {
    ChevronDownIcon,
    ChevronRightIcon,
    DocumentPlusIcon,        // New File
    ArrowUpOnSquareIcon,    // Open File
    FolderOpenIcon,         // Open Folder / Directory Item
    DocumentIcon,           // File Item
    ClockIcon,              // Recent Files Section
    FolderIcon,             // Folders Section
    ArrowPathIcon,          // Refresh / Loading
    XMarkIcon,              // Close/Remove
    InformationCircleIcon, // Info
    ChevronLeftIcon,         // Collapse Sidebar
    ChevronRightIcon as ExpandSidebarIcon,      // Expand Sidebar (using alias)
    ExclamationTriangleIcon, // Warning/Permission Needed
    // FolderPlusIcon
} from '@heroicons/react/24/outline'; // Or solid

// Replace Heroicons with Phosphor Icons
import {
    CaretDown,
    CaretRight,
    FilePlus,
    ArrowSquareUp,
    FolderOpen,
    File,
    Clock, 
    Folder,
    ArrowsClockwise,
    X,
    Info,
    CaretLeft,
    WarningCircle,
    MicrosoftExcelLogo,  // Use this instead of FileExcel
    Sidebar,
    FileMd,
    FileCsv
} from "@phosphor-icons/react";

// Helper function (can be moved to a utils file)
function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, initiallyOpen = true }) => {
    const [isOpen, setIsOpen] = useState(initiallyOpen);

    return (
        <div>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-2 py-2 text-[11px] font-medium text-gray-400 uppercase rounded hover:bg-lime-green-100 focus:outline-none"
            >
                <div className="flex items-center">
                    {Icon && <Icon className="w-4 h-4 mr-1.5" />}
                    <span>{title}</span>
                </div>
                {isOpen ? <CaretDown className="w-4 h-4" /> : <CaretRight className="w-4 h-4" />}
            </button>
            {isOpen && <div className="p-0 space-y-1">{children}</div>}
        </div>
    );
};

// --- Recursive Directory Tree Node Component ---
const DirectoryNode = observer(({
    node,
    level = 0,
    fileSystemStore,
    onOpenFileFromDirectory,
    onCreateFileInDirectory
}) => {
    if (!node) return null;

    const isDirectory = node.kind === 'directory';
    const isRoot = node.isRoot === true;
    const indent = isRoot ? 0 : level * 10;

    const isSelected = !isDirectory &&
        fileSystemStore.selectedFilePath === node.path.join('/');

    const isExcelFile = !isDirectory &&
        (node.name.toLowerCase().endsWith('.xlsx') || node.name.toLowerCase().endsWith('.xls'));
    const isMarkdownFile = !isDirectory && node.name.toLowerCase().endsWith('.md');
    const isCsvFile = !isDirectory && node.name.toLowerCase().endsWith('.csv');

    const handleToggleExpand = (e) => {
        e.stopPropagation();
        if (isDirectory) {
            fileSystemStore.toggleExpandNode(node);
        }
    };

    const handleNodeClick = () => {
        if (isDirectory) {
            fileSystemStore.toggleExpandNode(node);
        } else {
            onOpenFileFromDirectory(node);
        }
    };

    // --- New Handler for Create File Button ---
    const handleCreateClick = (e) => {
        e.stopPropagation(); // Prevent directory toggle/open
        if (isDirectory && node.handle && onCreateFileInDirectory) {
            console.log("Create file button clicked for directory:", node.name);
            onCreateFileInDirectory(node); // Pass the whole node (contains handle and path)
        }
    };

    return (
        <li className="flex flex-col">
            <button
                onClick={handleNodeClick}
                // Add group class here for hover effect on button
                className={`relative group mb-0.5 w-full text-left py-1.5 flex items-center rounded-md
                    ${isSelected ? 'bg-lime-green-200' : 'hover:bg-lime-green-100'}`}
                title={node.name}
                style={{ paddingLeft: `${indent}px` }}
            >
                 {/* Indentation Spacer */}
                 {/* <div style={{ width: `${indent}px` }} className="flex-shrink-0"></div> */}

                {/* Toggle Icon */}
                <span className="w-5 h-5 mr-0.5 flex items-center justify-center flex-shrink-0">
                    {isDirectory ? (
                        <span onClick={handleToggleExpand} className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer">
                            {node.isLoading ? (
                                <ArrowsClockwise className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                            ) : node.isExpanded ? (
                                <CaretDown className="w-3.5 h-3.5 text-gray-500"/>
                            ) : (
                                <CaretRight className="w-3.5 h-3.5 text-gray-500"/>
                            )}
                        </span>
                    ) : (
                        <span className="w-3.5 h-3.5"></span> // Placeholder for alignment
                    )}
                </span>

                {/* Main Icon */}
                <span className="w-5 h-5 mr-1 flex items-center justify-center flex-shrink-0">
                     {isDirectory ? (
                        <Folder weight="duotone" className={`w-4 h-4 ${node.isExpanded ? 'text-lime-green-950' : 'text-lime-green-950'}`} />
                    ) : isExcelFile ? (
                        <MicrosoftExcelLogo weight="fill" className={`w-4 h-4 ${isSelected ? 'text-lime-green-950' : 'text-lime-green-700'}`} />
                    ) : isMarkdownFile ? (
                        <FileMd weight="fill" className={`w-4 h-4 ${isSelected ? 'text-purple-700' : 'text-purple-600'}`} />
                    ) : isCsvFile ? (
                        <FileCsv weight="fill" className={`w-4 h-4 ${isSelected ? 'text-orange-700' : 'text-orange-600'}`} />
                    ) : (
                        <File className={`w-4 h-4 ${isSelected ? 'text-lime-green-9500' : 'text-lime-green-950'}`} />
                    )}
                </span>

                {/* Name */}
                <span className={`flex-1 truncate text-sm ${isSelected ? 'text-lime-green-950 font-medium' : ''}`}>
                    {node.name}
                </span>

                 {/* Optional: Error indicator */}
                {node.error && (
                    <WarningCircle className="w-4 h-4 text-red-500 ml-1 flex-shrink-0" title={node.error}/>
                )}

                 {/* --- New Create File Button (only for directories) --- */}
                 {/* {isDirectory && !isRoot && node.handle && ( // Don't show for root, ensure handle exists
                     <span
                         onClick={handleCreateClick}
                         title={`Create new file in ${node.name}`}
                         // Show on hover of the parent button (group-hover)
                         className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-lime-green-200 text-lime-green-700 hover:text-lime-green-900 transition-opacity duration-150"
                    >
                        <FilePlus weight="duotone" className="w-4 h-4" />
                     </span>
                 )} */}
            </button>

            {/* Render Children if Expanded */}
            {isDirectory && node.isExpanded && node.children && (
                <ul className="pl-0">
                    {node.children.length === 0 && !node.isLoading && (
                        <li className="text-xs text-gray-400 italic" style={{ paddingLeft: `${indent + 8 + 20 + 4}px` }}>
                            (empty)
                        </li>
                    )}
                    {node.children.map((child) => (
                        <DirectoryNode
                            key={child.handle?.name || child.name}
                            node={child}
                            level={isRoot ? 1 : level + 1}
                            fileSystemStore={fileSystemStore}
                            onOpenFileFromDirectory={onOpenFileFromDirectory}
                            onCreateFileInDirectory={onCreateFileInDirectory} // <-- Pass down
                        />
                    ))}
                </ul>
            )}
        </li>
    );
});

export const WorkbookSidebar = observer(({
    viewModel,
    isExpanded,
    toggleSidebar,
    // Add handlers passed from WorkbookView
    onCreateNew,
    onOpenFile,
    onOpenFolder,
    onOpenRecent,
    onOpenFromDirectory,
    // --- Add the new handler ---
    onCreateFileInDirectory, // This will call viewModel.handleCreateFileInDirectory
    // Pass stores directly for easier access
    fileSystemStore,
    settingsStore
}) => {
    // Local state for section visibility
    // const [isRecentFilesExpanded, setIsRecentFilesExpanded] = useState(true); // Use CollapsibleSection instead
    // const [isFoldersExpanded, setIsFoldersExpanded] = useState(true); // Use CollapsibleSection instead

    // Get necessary data from stores
    const recentFiles = settingsStore?.settings?.recentFiles || [];
    const hasDirectoryAccess = !!fileSystemStore?.directoryHandle;
    const directoryTreeRoot = fileSystemStore?.directoryTree;
    const isLoadingRoot = fileSystemStore?.isLoadingDirectory || directoryTreeRoot?.isLoading;
    const recentlyAccessedDirectory = fileSystemStore?.recentlyAccessedDirectory;
    const recentDirectories = fileSystemStore?.recentDirectories || [];
    const isFileSystemSupported = fileSystemStore?.isFileSystemAccessSupported() || false;
    const supportsPersistentPermissions = fileSystemStore?.supportsPersistentPermissions() || false;

    // State for permission handling within the sidebar
    const [hasCheckedPermission, setHasCheckedPermission] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [showPermissionInfo, setShowPermissionInfo] = useState(false); // For the info popup

    // Check user data permission status on mount/when support is known
    useEffect(() => {
        if (!isFileSystemSupported) {
            setHasCheckedPermission(true);
            setHasPermission(true);
            return;
        }
        
        let isMounted = true; // Add this flag
        
        const check = async () => {
            try {
                const permission = await viewModel.checkUserDataPermission();
                if (isMounted) { // Only update state if component is still mounted
                    setHasPermission(permission);
                    setHasCheckedPermission(true);

                    if (permission && fileSystemStore?.directoryHandle && 
                        !fileSystemStore?.directoryTree?.name && 
                        typeof fileSystemStore.buildDirectoryTree === 'function') {
                        console.log("Permission granted but directory tree not loaded, attempting to rebuild");
                        fileSystemStore.buildDirectoryTree(fileSystemStore.directoryHandle);
                    }
                }
            } catch (err) {
                console.error("Error checking permission:", err);
                if (isMounted) {
                    setHasCheckedPermission(true);
                    setHasPermission(false);
                }
            }
        };
        check();
        
        return () => { isMounted = false; }; // Cleanup function
    }, [viewModel, isFileSystemSupported, fileSystemStore]);

    // Additional effect to ensure directory tree is built when directory handle changes
    useEffect(() => {
        if (fileSystemStore.directoryHandle && !fileSystemStore.directoryTree.name) {
            console.log("Directory handle exists but tree not built, rebuilding tree");
            fileSystemStore.buildDirectoryTree(fileSystemStore.directoryHandle);
        }
    }, [fileSystemStore.directoryHandle, fileSystemStore.directoryTree.name]);

    // Handler for when user clicks Allow/Deny in the permission prompt
    const handlePermissionResponse = (granted) => {
        viewModel.setUserDataPermission(granted); // Update localStorage via viewmodel
        setHasPermission(granted); // Update local state to re-render UI
    };

    const handleOpenRecentDirectory = async (dirEntry) => {
        // Need to ask ViewModel to handle this, potentially with unsaved changes check
        // For now, directly call fileSystemStore method assuming ViewModel handles checks
        // await viewModel.handleOpenRecentDirectory(dirEntry); // Create this method in ViewModel if needed
        await fileSystemStore.tryRestoreDirectoryAccess(dirEntry.handle);
    };

    const handleRemoveRecentDirectory = async (e, dirId) => {
        e.stopPropagation();
        // Ask fileSystemStore to remove it
        await fileSystemStore.removeRecentDirectory(dirId);
    };

    // Determine if the permission request UI should be shown inside the sidebar sections
    const showPermissionRequestUI = isFileSystemSupported && hasCheckedPermission && !hasPermission;

    // TODO: Migrate WorkbookSelector logic here (Recent Files, Directory Browser)
    // TODO: Implement collapsible sections, better icons, etc.

    // Get necessary data from viewModel or stores directly if needed
    // const { fileSystemStore, settingsStore } = viewModel.stores; // Example access

    // Add this new effect to the WorkbookSidebar component:
    // This will force a re-render when the tree structure changes
    useEffect(() => {
        // Log when the directory tree changes to help with debugging
        if (directoryTreeRoot && directoryTreeRoot.children) {
            console.log('Directory tree updated in UI:', {
                name: directoryTreeRoot.name,
                childCount: directoryTreeRoot.children.length
            });
        }
    }, [directoryTreeRoot, directoryTreeRoot?.children?.length]);

    if (!isExpanded) {
        // --- Collapsed Sidebar View ---
        return (
            <div className="transition-width duration-300 ease-in-out border-r border-gray-200 h-screen flex flex-col w-16 items-center">
                {/* Toggle Button */}
                <div className="p-2 w-full flex justify-center border-b border-gray-200 mb-3">
                    <button
                        onClick={toggleSidebar}
                        className="p-2 rounded text-lime-green-950 hover:bg-lime-green-100 hover:text-lime-green-950"
                        title="Expand Sidebar"
                    >
                         <Sidebar weight="duotone" className="w-5 h-5" />
                    </button>
                </div>
                {/* Action Icons */}
                <div className="space-y-3 flex flex-col items-center">
                    <button onClick={onCreateNew} className="p-2 rounded hover:bg-lime-green-100" title="New File">
                        <FilePlus weight="duotone" className="w-4 h-4 text-lime-green-950" />
                    </button>
                    <button onClick={onOpenFile} className="p-2 rounded hover:bg-lime-green-100" title="Open File...">
                        <ArrowSquareUp weight="duotone" className="w-4 h-4 text-lime-green-950" />
                    </button>
                    {isFileSystemSupported && (
                        <button onClick={onOpenFolder} className="p-2 rounded hover:bg-lime-green-100" title="Open Folder...">
                            <FolderOpen weight="duotone" className="w-4 h-4 text-lime-green-950" />
                        </button>
                    )}
                     {/* Optional: Icons for recent/folders if needed */}
                     {/* <button className="p-2 rounded hover:bg-gray-200" title="Recent Files"><ClockIcon className="w-6 h-6 text-gray-700" /></button> */}
                     {/* <button className="p-2 rounded hover:bg-gray-200" title="Folders"><FolderIcon className="w-6 h-6 text-gray-700" /></button> */}
                </div>
            </div>
        );
    }

    // --- Expanded Sidebar View ---
    return (
        <div className="transition-width duration-300 ease-in-out border-r border-gray-200 h-screen flex flex-col w-64 min-w-64 max-w-64">
            {/* Header with Toggle Button */}
            <div className="p-2 flex items-center justify-between border-b border-gray-200 flex-shrink-0">
                 {/* Optional: Add a title or logo here */}
                 <span className="text-lg font-semibold px-2">Spreadable</span>
                <button
                    onClick={toggleSidebar}
                    className="p-2 rounded text-lime-green-950 hover:bg-lime-green-100 hover:text-lime-green-950"
                    title="Collapse Sidebar"
                 >
                     <Sidebar weight="duotone" className="w-5 h-5" />
                </button>
            </div>

            {/* Main Scrollable Content with a fixed width container */}
            <div className="flex-grow overflow-y-auto overflow-x-hidden p-1.5 space-y-1 w-full">
                {/* Action Buttons */}
                <div className="space-y-1">
                    <button
                        onClick={onCreateNew}
                        className="w-full flex items-center px-3 py-2 text-sm text-left text-lime-green-950 rounded-md hover:bg-lime-green-100"
                    >
                        <FilePlus fill="duotone" className="w-4 h-4 mr-2 flex-shrink-0 text-lime-green-950" /> New File
                    </button>
                    <button
                        onClick={onOpenFile}
                        className="w-full flex items-center px-3 py-2 text-sm text-left text-lime-green-950 rounded-md hover:bg-lime-green-100"
                    >
                        <ArrowSquareUp fill="duotone" className="w-4 h-4 mr-2 flex-shrink-0 text-lime-green-950" /> Open File...
                    </button>
                    {isFileSystemSupported && (
                        <button
                            onClick={onOpenFolder}
                            className="w-full flex items-center px-3 py-2 text-sm text-left text-lime-green-950 rounded-md hover:bg-lime-green-100"
                        >
                            <FolderOpen fill="duotone" className="w-4 h-4 mr-2 flex-shrink-0 text-lime-green-950" /> Select Folder...
                        </button>
                    )}
                </div>

                 {/* --- Permission Request UI --- */}
                 {showPermissionRequestUI && (
                     <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                         <h3 className="font-medium text-blue-800 mb-1">Remember Files & Folders?</h3>
                         <p className="text-xs text-blue-700 mb-2">
                             Allow storing recent files and directory access locally on your device for faster access next time.
                         </p>
                         <div className="flex space-x-2">
                             <button
                                 onClick={() => handlePermissionResponse(true)}
                                 className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                             >
                                 Allow
                             </button>
                             <button
                                 onClick={() => handlePermissionResponse(false)}
                                 className="px-2 py-1 border border-blue-300 text-blue-700 text-xs rounded hover:bg-blue-100"
                             >
                                 No Thanks
                             </button>
                         </div>
                     </div>
                 )}

                 {/* --- Recent Files Section --- */}
                <CollapsibleSection title="Recent Files" initiallyOpen={false}>
                     {!hasCheckedPermission ? (
                        <div className="text-center text-gray-500 text-xs py-2">Checking...</div>
                     ) : !hasPermission ? (
                          <div className="text-xs text-gray-500 px-2 py-1 italic">Allow storage to see recent files.</div>
                     ) : recentFiles.length > 0 ? (
                        <>
                            <ul className="space-y-0.5">
                                {recentFiles.map((file, index) => {
                                    const isExcelFile = file.name.toLowerCase().endsWith('.xlsx') || 
                                                      file.name.toLowerCase().endsWith('.xls');
                                    return (
                                        <li key={file.id || index}>
                                            <button
                                                onClick={() => onOpenRecent(file)}
                                                className="w-full text-left px-2 py-1.5 rounded hover:bg-lime-green-100 flex items-center group"
                                                title={file.directoryPath ? `${file.directoryPath}/${file.relativePath}` : file.name}
                                            >
                                                {isExcelFile ? (
                                                    <MicrosoftExcelLogo weight="fill" className="w-4 h-4 mr-1.5 text-lime-green-700 flex-shrink-0" />
                                                ) : file.name.toLowerCase().endsWith('.md') ? (
                                                    <FileMd weight="fill" className="w-4 h-4 mr-1.5 text-purple-600 flex-shrink-0" />
                                                ) : file.name.toLowerCase().endsWith('.csv') ? (
                                                    <FileCsv weight="fill" className="w-4 h-4 mr-1.5 text-orange-600 flex-shrink-0" />
                                                ) : (
                                                    <File className="w-4 h-4 mr-1.5 text-lime-green-700 flex-shrink-0" />
                                                )}
                                                <div className="flex-1 truncate text-sm text-lime-green-950">
                                                    {file.name}
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                             <button
                                onClick={() => settingsStore.clearRecentFiles()}
                                className="text-xs text-gray-400 hover:text-gray-700 px-2 py-1"
                            >
                                Clear recent files
                            </button>
                        </>
                     ) : (
                        <div className="text-xs text-gray-500 px-2 py-1 italic">No recent files.</div>
                     )}
                 </CollapsibleSection>


                {/* --- Folders Section (Tree View) --- */}
                 {isFileSystemSupported && (
                     <CollapsibleSection title="Folders" initiallyOpen={true}>
                         {!hasCheckedPermission ? (
                             <div className="text-center text-gray-500 text-xs py-2">Checking...</div>
                         ) : !hasPermission ? (
                             <div className="text-xs text-gray-500 px-2 py-1 italic">Allow storage to access folders.</div>
                         ) : hasDirectoryAccess && directoryTreeRoot ? (
                             <div className="text-sm">
                                 <ul className="space-y-0">
                                     {isLoadingRoot ? (
                                         <div className="flex items-center p-2 text-gray-500">
                                            <ArrowsClockwise className="w-4 h-4 animate-spin mr-1"/> Loading...
                                         </div>
                                     ) : (
                                         <DirectoryNode
                                             node={directoryTreeRoot}
                                             level={0}
                                             fileSystemStore={fileSystemStore}
                                             onOpenFileFromDirectory={onOpenFromDirectory}
                                             onCreateFileInDirectory={onCreateFileInDirectory}
                                         />
                                     )}
                                 </ul>
                             </div>
                         ) : (
                             // --- No Directory Access UI ---
                             <div className="space-y-2">
                                 {/* Restore Access Prompt */}
                                 {recentlyAccessedDirectory && (
                                     <div className="mb-2 p-2 bg-yellow-50 text-yellow-800 rounded-md text-xs border border-yellow-200 flex items-start">
                                         <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0"/>
                                        <div>
                                             Restore access to "{recentlyAccessedDirectory}"?
                                             <button
                                                 onClick={() => fileSystemStore.attemptAutoRestore()}
                                                 className="ml-1 text-yellow-900 font-medium hover:underline"
                                             >
                                                 Restore
                                             </button>
                                         </div>
                                     </div>
                                 )}
                                 {/* Recent Directories List */}
                                 {recentDirectories.length > 0 && (
                                     <div className="space-y-0.5">
                                         <span className="text-xs text-gray-500 block px-2 mb-1">Recent Locations:</span>
                                         {recentDirectories.map(dir => (
                                             <button
                                                 key={dir.id}
                                                 onClick={() => handleOpenRecentDirectory(dir)}
                                                 className="w-full text-left px-2 py-1 rounded hover:bg-gray-200 flex items-center group"
                                                 title={`Restore access to ${dir.name}`}
                                             >
                                                 <FolderIcon className="w-4 h-4 mr-1.5 text-gray-500 flex-shrink-0" />
                                                 <span className="flex-1 truncate text-sm text-gray-700">{dir.name}</span>
                                                 <button
                                                     onClick={(e) => handleRemoveRecentDirectory(e, dir.id)}
                                                     className="opacity-0 group-hover:opacity-100 ml-1 p-0.5 rounded text-gray-400 hover:bg-gray-300 hover:text-gray-600"
                                                     title="Remove from list"
                                                 >
                                                     <XMarkIcon className="w-3 h-3"/>
                                                 </button>
                                             </button>
                                         ))}
                                     </div>
                                 )}
                                 {/* Button to select a new folder */}
                                 <button
                                     onClick={onOpenFolder}
                                     className="w-full flex items-center justify-center px-3 py-1.5 text-sm text-gray-700 bg-white rounded border border-gray-300 hover:bg-gray-50"
                                 >
                                     <FolderOpenIcon className="w-4 h-4 mr-1" /> Select Folder...
                                 </button>
                                 {/* Persistent Permission Info */}
                                 {!supportsPersistentPermissions && (
                                     <div className="flex items-center justify-center mt-1">
                                         <button onClick={() => setShowPermissionInfo(true)} className="p-1 text-gray-400 hover:text-gray-600" title="Why grant access each session?">
                                             <InformationCircleIcon className="w-4 h-4"/>
                                         </button>
                                     </div>
                                 )}

                    </div>
                )}
                    </CollapsibleSection>
                 )}

            </div>

             {/* Optional: Sidebar Footer */}
             {/* <div className="p-2 border-t border-gray-200 flex-shrink-0"> ... </div> */}

             {/* --- Permission Info Modal (Simple Example) --- */}
             {showPermissionInfo && (
                 <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowPermissionInfo(false)} // Close on backdrop click
                 >
                     <div className="bg-white p-5 rounded-lg shadow-xl max-w-sm" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-2">Folder Access Permissions</h3>
                        <p className="text-sm text-gray-700 mb-3">
                            Your browser requires you to grant access to folders each time you start a new session for security reasons. Some browsers might support remembering this permission in the future.
                        </p>
                        <button
                            onClick={() => setShowPermissionInfo(false)}
                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300"
                         >
                             Got it
                        </button>
                     </div>
                 </div>
             )}
        </div>
    );
});